import React, { useContext, useEffect, useState } from "react";
import { IdentifierMappingContext } from "../IdentifierMappingContext";
import { BaseModal } from "../../common/Modal/BaseModal";
import { Close } from "@material-ui/icons";
import { FormControl, MenuItem, InputLabel, Select, TextField } from "@mui/material";
import plantsIllustration from "../../../../assets/images/plants_illustration.png";
import { displayResponseErrors } from "../../utils/Helpers";
import { AddressSearchInput } from "../../common/AddressSearchInput";
import MixpanelService from "../../utils/MixpanelService";
import { useIdentifierMappingSubmit } from "../Hooks/useIdentifierMappingSubmit";

const defaultForm = {
  identifier_type: "",
  pharmacy_name: "",
  pharmacy_address: "",
  identifier: "",
};

export const AddIdentifierModal = () => {
  const {
    addIdentifierModalOpen,
    setAddIdentifierModalOpen,
    activePharmacyGroup,
    coveredEntity340bId,
  } = useContext(IdentifierMappingContext);

  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    setForm(defaultForm);
  }, [addIdentifierModalOpen]);

  const { handleSubmit, submitting } = useIdentifierMappingSubmit({
    url: "/api/mapping_requests",
    body: {
      id_340b: coveredEntity340bId,
      group_name: activePharmacyGroup,
      ...form,
    },
    successMessage: "Add Identifier request submitted.",
    onSubmitSuccess: () => {
      setAddIdentifierModalOpen(false);
      setForm(defaultForm);
    },
  });

  // if we provided a pharmacy to this modal.
  const providedPharmacy = addIdentifierModalOpen?.pharmacy;

  useEffect(() => {
    if (providedPharmacy) {
      setForm((form) => ({
        ...form,
        pharmacy_name: providedPharmacy.name,
        pharmacy_public_id: providedPharmacy.public_id,
        pharmacy_id: providedPharmacy.id,
        pharmacy_address: providedPharmacy.address,
      }));
    }
  }, [providedPharmacy]);

  return (
    <BaseModal
      open={!!addIdentifierModalOpen}
      onClose={() => setAddIdentifierModalOpen(false)}
      className="modal-center"
      width={470}
    >
      <div
        className="bg--light-navy py-4 px--32 flex align-items-center border--rounded-top justify-content-end"
        style={{ height: 80 }}
      >
        <img
          className="mt--32"
          style={{ width: 145, userSelect: "none", marginRight: 70 }}
          src={plantsIllustration}
          alt="plants_illustration"
        />

        <button
          className="button button--transparent border--none w--32 h--32"
          onClick={() => {
            MixpanelService.track("esp_horizon_add_identifier_cancelled");
            setAddIdentifierModalOpen(undefined);
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </button>
      </div>
      <div className="p--32">
        <div className="t--500 t--xl">Add Identifier</div>
        <div className="mt-2 t--md">
          Add an identifier into this pharmacy group. If the identifier already exists in our
          database and is in another group, this action will move the identifier to this group. This
          action will impact any associated current and future transactions with the identifier.
        </div>

        {providedPharmacy && (
          <div className="mt-4">
            <div className="px--12">
              <div className="t--sm t--secondary ml-1">Pharmacy Name *</div>
              <div className="my-3">{form.pharmacy_name}</div>
            </div>

            <div className="px--12">
              <div className="t--sm t--secondary ml-1">Pharmacy Address *</div>
              <div className="my-3">{form.pharmacy_address}</div>
            </div>
          </div>
        )}

        <div className="gap--16 grid grid-cols-2 mt-4">
          <TextField
            label="Identifier"
            size="medium"
            value={form.identifier}
            onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            margin="none"
            variant="outlined"
            fullWidth
            required
          />
          <FormControl variant="outlined" margin="none" size="medium" fullWidth>
            <InputLabel id="identifier-type-label" shrink>
              Type *
            </InputLabel>
            <Select
              fullWidth
              required
              size="medium"
              variant="outlined"
              label="Type"
              placeholder={"Choose Identifier Type"}
              notched
              value={form.identifier_type || ""}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: "#aaa" }}>Choose Identifier Type</span>;
                }
                return selected.toUpperCase();
              }}
              onChange={(e) => {
                const identifierType = e.target.value;

                MixpanelService.track("esp_horizon_identifier_type", {
                  identifier_type: identifierType,
                });
                setForm({ ...form, identifier_type: identifierType });
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              }}
            >
              <MenuItem value="npi">NPI</MenuItem>
              <MenuItem value="dea">DEA</MenuItem>
              <MenuItem value="hin">HIN</MenuItem>
            </Select>
          </FormControl>
        </div>

        {!providedPharmacy && (
          <>
            <div className="mt-3">
              <TextField
                fullWidth
                variant="outlined"
                value={form.pharmacy_name}
                onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })}
                required
                label="Pharmacy Name"
                placeholder="Pharmacy Name"
              />
            </div>
            <div className="mt-3">
              <AddressSearchInput
                type={activePharmacyGroup === "in_house" ? "in_house" : "cp"}
                id_340b={coveredEntity340bId}
                value={
                  form.pharmacy_id
                    ? {
                        address: form.pharmacy_address,
                        pharmacy_id: form.pharmacy_id,
                        pharmacy_public_id: form.pharmacy_public_id,
                        pharmacy_name: form.pharmacy_name,
                      }
                    : null
                }
                onChange={(event, pharmacy) => {
                  setForm({
                    ...form,
                    pharmacy_address: pharmacy?.address,
                    pharmacy_id: pharmacy?.pharmacy_id,
                    pharmacy_public_id: pharmacy?.pharmacy_public_id,
                    pharmacy_name: pharmacy?.name ?? form.pharmacy_name,
                  });
                }}
                required
                label="Pharmacy Address *"
                placeholder="Search addresses"
              />
            </div>
          </>
        )}

        <div className="flex align-items-center justify-content-end mt-4 gap--8">
          <button
            className="button button--white"
            onClick={() => {
              MixpanelService.track("esp_horizon_add_identifier_cancelled");
              setAddIdentifierModalOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            className="button button--blue"
            disabled={
              !form.identifier ||
              !form.identifier_type ||
              !form.pharmacy_address ||
              !form.pharmacy_name ||
              submitting
            }
            onClick={() => handleSubmit()}
          >
            Submit for Approval
          </button>
        </div>
      </div>
    </BaseModal>
  );
};
